/**
 * WebSocket Service for Real-time Notifications
 * Handles user-specific notifications with automatic reconnection
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 5000;
    this.listeners = new Map();
    this.isConnected = false;
    this.userID = null;
    this.pingInterval = null;
    this.connectionTimeout = null;
    this.connectionTimeoutDuration = 10000; // 10 seconds
  }

  /**
   * Connect to WebSocket server with user authentication
   */
  connect(token, userID) {
    if (this.ws && this.isConnected) {
      return;
    }

    // Clear any existing connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }

    this.userID = userID;
    // Use production WebSocket URL - convert https to wss protocol
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_BACKEND_PROXY_URL || 'https://AutoboyExpress-go.onrender.com';
    const baseUrl = apiUrl.replace('/api/v1', '').replace('http://', 'ws://').replace('https://', 'wss://');
    const wsUrl = import.meta.env.VITE_WS_URL || `${baseUrl}/api/v1/ws/connect`;
    const url = `${wsUrl}?token=${token}`;

    try {
      this.ws = new WebSocket(url);

      // Set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
          this.handleConnectionFailure('Connection timeout');
        }
      }, this.connectionTimeoutDuration);

      this.ws.onopen = () => {
        // Clear connection timeout on successful connection
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
 
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Start ping interval to keep connection alive
        this.startPingInterval();

        // Notify listeners about connection
        this.notifyListeners('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle different message types
          this.handleMessage(message);
        } catch (error) {
          // Silently handle JSON parse errors
        }
      };

      this.ws.onerror = (error) => {
        // Suppress WebSocket error logs to console - backend will handle reconnection
        this.handleConnectionFailure('WebSocket error');
      };

      this.ws.onclose = (event) => {
        this.isConnected = false;
        this.stopPingInterval();

        // Only treat as auth error if we get explicit policy violation (1008)
        // Status code 1006 is generic and can be network/CORS issues, not just auth
        if (event.code === 1008) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          alert('Your session has expired. Please log in again to receive real-time notifications.');
          window.location.href = '/auth';
          return;
        }

        // For 1006 errors, just try to reconnect - don't assume it's auth related
        // Could be CORS, network issues, server down, etc.
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 10000);

          setTimeout(() => {
            // Get fresh token from localStorage in case it was refreshed
            const currentToken = localStorage.getItem('authToken');
            if (currentToken && userID) {
              this.connect(currentToken, userID);
            } else {
            }
          }, delay);
        } else {
          this.notifyListeners('disconnected', { reason: 'Max reconnection attempts reached' });
        }
      };
    } catch (error) {
      // Silently handle WebSocket creation errors
      this.handleConnectionFailure(error?.message || 'Failed to create WebSocket');
    }
  }

  /**
   * Handle WebSocket connection failure with graceful degradation
   */
  handleConnectionFailure(reason) {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    this.isConnected = false;

    // Attempt to reconnect if we haven't exceeded max attempts
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * this.reconnectAttempts;

      setTimeout(() => {
        const currentToken = localStorage.getItem('authToken');
        const userID = this.userID;
        if (currentToken && userID) {
          this.connect(currentToken, userID);
        }
      }, delay);
    } else {
      // Max reconnection attempts reached - gracefully degrade
      // App will continue to work without real-time notifications
      this.notifyListeners('disconnected', { reason: 'Real-time notifications unavailable' });
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.ws) {
      this.stopPingInterval();
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.userID = null;
    }
  }

  /**
   * Send message to server
   */
  send(type, data) {
    if (this.ws && this.isConnected) {
      const message = {
        type,
        data,
        timestamp: new Date().toISOString()
      };

      this.ws.send(JSON.stringify(message));
    } else {
    }
  }

  /**
   * Handle incoming messages
   */
  handleMessage(message) {
    const { type, data } = message;

    switch (type) {
      case 'connection_established':
        break;

      case 'notification':
        this.notifyListeners('notification', data);
        break;

      case 'order_status_update':
        this.notifyListeners('order_update', data);
        break;

      case 'new_message':
        this.notifyListeners('new_message', data);
        break;

      case 'price_alert_triggered':
        this.notifyListeners('price_alert', data);
        break;

      case 'dispute_update':
        this.notifyListeners('dispute_update', data);
        break;

      case 'product_update':
        this.notifyListeners('product_update', data);
        break;

      case 'payment_update':
        this.notifyListeners('payment_update', data);
        break;

      case 'wallet_update':
        this.notifyListeners('wallet_update', data);
        break;

      case 'pong':
        // Pong response to ping - connection alive, no action needed
        break;

      default:
        this.notifyListeners(type, data);
    }
  }

  /**
   * Subscribe to specific event type
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(eventType, data) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  startPingInterval() {
    this.pingInterval = setInterval(() => {
      if (this.ws && this.isConnected) {
        this.send('ping', {});
      }
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      userID: this.userID,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Join a specific room (e.g., conversation room)
   */
  joinRoom(roomID) {
    this.send('join_room', { room_id: roomID });
  }

  /**
   * Leave a specific room
   */
  leaveRoom(roomID) {
    this.send('leave_room', { room_id: roomID });
  }

  /**
   * Chat-specific methods
   */

  // Join a conversation for real-time updates
  joinConversation(conversationId) {
    this.send('join_conversation', { conversation_id: conversationId });
  }

  // Leave a conversation
  leaveConversation(conversationId) {
    this.send('leave_conversation', { conversation_id: conversationId });
  }

  // Send a chat message via WebSocket
  sendChatMessage(conversationId, content, type = 'text') {
    this.send('chat_message', {
      conversation_id: conversationId,
      content,
      type
    });
  }

  // Start typing indicator
  startTyping(conversationId) {
    this.send('typing_start', { conversation_id: conversationId });
  }

  // Stop typing indicator
  stopTyping(conversationId) {
    this.send('typing_stop', { conversation_id: conversationId });
  }

  // Send message reaction
  sendReaction(messageId, conversationId, emoji) {
    this.send('message_reaction', {
      message_id: messageId,
      conversation_id: conversationId,
      emoji
    });
  }

  // Remove message reaction
  removeReaction(messageId, conversationId, emoji) {
    this.send('remove_reaction', {
      message_id: messageId,
      conversation_id: conversationId,
      emoji
    });
  }

  // Mark messages as read
  markAsRead(conversationId) {
    this.send('mark_read', { conversation_id: conversationId });
  }

  // Mark messages as delivered
  markAsDelivered(conversationId, messageIds) {
    this.send('mark_delivered', {
      conversation_id: conversationId,
      message_ids: messageIds
    });
  }

  // Share product in chat
  shareProduct(conversationId, productId) {
    this.send('share_product', {
      conversation_id: conversationId,
      product_id: productId
    });
  }

  // Share order update in chat
  shareOrderUpdate(conversationId, orderId, status, message) {
    this.send('share_order_update', {
      conversation_id: conversationId,
      order_id: orderId,
      status,
      message
    });
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
